import { useTheme } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { Bell, Inbox, InboxContent } from '@novu/react';
import { useGetByDevice } from '@onesource/components';
import { MembersBellIcon } from '@onesource/icons';
import { ENovuTenantIdentifierEnum, GenerateSubscriberIdWithTenantSchema } from '@onesource/schemas';
import { getSession, useGetAllProfileQuery } from '@onesource/services';
import { formatDistanceToNow } from 'date-fns';
import DomPurify from 'dompurify';
import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { NOVU_APPLICATION_IDENTIFIER } from 'src/config';

export const NotificationCenter: FC = () => {
  const token = getSession();
  const { data, status } = useGetAllProfileQuery({ token });
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const handleOpen = (event: SyntheticEvent<Element, Event>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const { isMobile } = useGetByDevice();

  const subscriberId = useMemo(() => {
    if (!data?.id || status !== 'success') {
      return null;
    }

    return GenerateSubscriberIdWithTenantSchema.parse({
      tenantIdentifier: ENovuTenantIdentifierEnum.members,
      userId: data.id,
    });
  }, [data?.id, status]);

  if (!subscriberId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        alignItems: 'center',
      }}
      zIndex={9999}
    >
      <Inbox
        applicationIdentifier={NOVU_APPLICATION_IDENTIFIER}
        subscriberId={subscriberId}
        preferenceGroups={[{ name: ENovuTenantIdentifierEnum.members, filter: { tags: ['members'] } }]}
        appearance={{
          elements: {
            inboxContent: {
              width: '332px',
              height: '370px',
              overflowX: 'hidden',
            },
            inboxHeader: {
              paddingTop: '14px',
              paddingBottom: '6px',
              background: 'transparent',
            },
            inboxStatus__title: {
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '16px',
              color: theme.palette.grey[900],
            },
            notificationListContainer: {
              border: 'none',
            },
            notification: {
              gap: '14px',
              padding: '0px 20px 4px 16px',
            },
            notificationContent: {
              gap: '2px',
              paddingBottom: '6px',
              borderBottom: '1px solid #D9D9D9',
            },
            notificationImage: {
              width: '30px',
              height: '30px',
              borderRadius: '50%',
            },
            notificationDate: {
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '16px',
              color: theme.palette.grey[400],
            },
            notificationDot: {
              backgroundColor: theme.palette.primary.main,
            },
            preferences__button: {
              display: 'none',
            },
          },
        }}
      >
        <Bell
          renderBell={unreadCount => (
            <IconButton disableRipple onClick={handleOpen} sx={{ px: 1 }}>
              <Badge
                badgeContent={unreadCount.total}
                color="error"
                sx={{
                  '& .MuiBadge-badge': { mt: -0.6, width: '100%', height: '100%', fontSize: 10 },
                }}
              >
                <MembersBellIcon color={theme.palette.primary.main} />
              </Badge>
            </IconButton>
          )}
        />
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: isMobile ? 'center' : 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                mt: { xs: 1, md: 2 },
                width: '332px',
                height: '370px',
                maxHeight: 'auto',
                borderRadius: '16px',
                background: theme.palette.background.paper,
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          <InboxContent
            renderNotification={notification => {
              const handleClick = async () => {
                // Defensive: @novu/react 3.10.1 sometimes loses the Notification
                // class prototype, making notification.read a non-function. Guard
                // the call so navigation still happens on click.
                try {
                  if (!notification.isRead && typeof notification.read === 'function') {
                    await notification.read();
                  }
                } catch {
                  // swallow — don't let a failed read block navigation
                }
                const url = notification.redirect?.url;
                if (!url) {
                  handleClose();
                  return;
                }
                const target = notification.redirect?.target;
                handleClose();
                if (target === '_blank' || target === '_self' || target === '_parent' || target === '_top') {
                  window.open(url, target);
                } else if (/^https?:\/\//.test(url)) {
                  window.location.href = url;
                } else {
                  window.location.assign(url);
                }
              };
              const timeAgo = notification.createdAt
                ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                : '';
              return (
                <Box
                  onClick={handleClick}
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    padding: '10px 20px 10px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #D9D9D9',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  {notification.avatar ? (
                    <Avatar src={notification.avatar} sx={{ width: 30, height: 30, flexShrink: 0, mt: '2px' }} />
                  ) : (
                    <Box sx={{ width: 30, flexShrink: 0 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {notification.subject && (
                      <Box
                        sx={{
                          marginBottom: '2px',
                          fontSize: '11px',
                          fontWeight: 700,
                          lineHeight: '18px',
                          color: notification.isRead ? theme.palette.grey[600] : theme.palette.text.primary,
                        }}
                        dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(notification.subject) }}
                      />
                    )}
                    <Box
                      sx={{
                        fontSize: '11px',
                        fontWeight: 400,
                        lineHeight: '18px',
                        color: notification.isRead ? theme.palette.grey[600] : theme.palette.text.primary,
                      }}
                      dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(notification.body) }}
                    />
                    {timeAgo && (
                      <Typography
                        sx={{
                          mt: '2px',
                          fontSize: { xs: '10px', md: '10px' },
                          fontWeight: 400,
                          lineHeight: '16px',
                          color: theme.palette.grey[400],
                        }}
                      >
                        {timeAgo}
                      </Typography>
                    )}
                  </Box>
                  {!notification.isRead && (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        flexShrink: 0,
                        mt: '8px',
                      }}
                    />
                  )}
                </Box>
              );
            }}
          />
        </Popover>
      </Inbox>
    </Box>
  );
};
